import { uiText } from '../utils/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useStore } from '../store';
import s from './BriefingsPage.module.css';

const BRIEFING_FORMAT_VERSION = 2;
const BRIEFING_INTRO_VERSION = 'v3';

const C = {
  paper: '#f4eedb',
  paperLt: '#fbf7eb',
  ink: '#1b1b14',
  inkSoft: '#3c3a2b',
  olive: '#5a6a2a',
  oliveLt: '#b6c477',
  tomato: '#c54a2f',
  tomatoLt: '#f0a890',
  butter: '#c79a3a',
  sky: '#5a8ab8',
  plum: '#8a6ac8',
  mint: '#62b9a0',
  dim: 'rgba(27,27,20,0.55)',
};

const KIND_COLOR: Record<string, string> = {
  '平台公告': C.olive,
  '组织洞察': C.sky,
  '竞对与行业': C.tomato,
  '组织变动': C.plum,
  'Agent 周报': C.butter,
};

interface AgentAvatar {
  name: string;
  glyph: string;
  color: string;
}

interface BriefingItem {
  id: number;
  kind: string;
  ag: AgentAvatar;
  time: string;
  read: string;
  title: string;
  teaser: string;
  big: string;
  bigK: string;
  signals: Array<{ v: string; k: string; c: string }>;
  full: string[];
  points: string[];
  attach: string[];
  briefingVersion?: number;
}

interface ApiBriefing {
  id: string;
  issueNumber: number;
  formatVersion: number;
  tickerSignals: Array<{ key: string; value: string; color: string }>;
  items: BriefingItem[];
  createdAt: number;
}

interface PosterLayout {
  item: BriefingItem;
  solid: 'dark' | 'color' | 'plain';
  span: number;
  row: number;
  big: boolean;
  delay: number;
}

const LAYOUT_PRESETS: Omit<PosterLayout, 'item'>[] = [
  { solid: 'dark', span: 6, row: 2, big: true, delay: 0.05 },
  { solid: 'color', span: 6, row: 1, big: false, delay: 0.10 },
  { solid: 'plain', span: 3, row: 1, big: false, delay: 0.15 },
  { solid: 'plain', span: 3, row: 1, big: false, delay: 0.20 },
  { solid: 'color', span: 4, row: 1, big: false, delay: 0.25 },
  { solid: 'plain', span: 8, row: 1, big: false, delay: 0.30 },
];

function kindColor(kind: string): string {
  return KIND_COLOR[kind] || C.butter;
}

function introStorageKey(): string {
  const userId = localStorage.getItem('novax:userId') || 'anonymous';
  return `novax:briefing-intro:${BRIEFING_INTRO_VERSION}:${userId}`;
}

function hasSeenIntro(): boolean {
  try { return localStorage.getItem(introStorageKey()) === 'completed'; }
  catch { return false; }
}

function markIntroSeen(): void {
  try { localStorage.setItem(introStorageKey(), 'completed'); }
  catch { /* Storage can be unavailable in private mode. */ }
}

async function briefingRequest(token: string, path: string, method = 'GET'): Promise<ApiBriefing | null> {
  const response = await fetch(path, {
    method,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `简报请求失败（${response.status}）`);
  }
  const body = await response.json();
  return body.briefing || null;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function buildLayouts(items: BriefingItem[]): PosterLayout[] {
  return items.slice(0, LAYOUT_PRESETS.length).map((item, index) => ({
    ...LAYOUT_PRESETS[index],
    item,
  }));
}

function AgentDot({ icon, color, size = 22, onDark }: {
  icon: string;
  color: string;
  size?: number;
  onDark?: boolean;
}) {
  return <div
    className={s.agentDot}
    style={{ width: size, height: size, background: color, border: `1.5px solid ${onDark ? 'rgba(255,255,255,0.5)' : C.ink}` }}
  >
    <span className={s.agentDotLabel} style={{ fontSize: size * 0.44 }}>{icon}</span>
  </div>;
}

function KindTag({ kind, sm, onDark }: { kind: string; sm?: boolean; onDark?: boolean }) {
  const color = kindColor(kind);
  const labelColor = color === C.butter ? (onDark ? '#e7d9a8' : C.inkSoft) : color;
  return <div className={s.kindTag}>
    <span className={s.kindBar} style={{ width: sm ? 14 : 24, background: color }} />
    <span className={s.kindLabel} style={{ fontSize: sm ? 9.5 : 11, color: labelColor }}>{uiText(kind)}</span>
  </div>;
}

function Seal() {
  return <svg className={s.seal} width="58" height="58" viewBox="0 0 100 100" aria-hidden="true">
    <defs><path id="briefSealPath" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" fill="none" /></defs>
    <text style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, fontWeight: 700, letterSpacing: 2.5, fill: C.inkSoft }}>
      <textPath href="#briefSealPath" startOffset="0">WEEKLY · INTELLIGENCE · NOVAX · </textPath>
    </text>
    <circle cx="50" cy="50" r="5" fill={C.tomato} stroke={C.ink} strokeWidth="1.2" />
  </svg>;
}

function Clock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => {
      const date = new Date();
      const pad = (value: number) => String(value).padStart(2, '0');
      setTime(`${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, []);
  const now = new Date();
  return <span className={s.clockLine}>
    <span className={s.blinkDot} />
    {now.getFullYear()} · {now.getMonth() + 1} · {now.getDate()}
    <span className={s.clockDigits}>{time}</span>
  </span>;
}

function BriefingIntro({ currentReady, onEnter }: { currentReady: boolean; onEnter: () => void }) {
  return <div className={s.introRoot} role="dialog" aria-modal="true" aria-labelledby="briefing-intro-title">
    <div className={s.introBack} />
    <section className={s.introSheet}>
      <div className={s.introHalftone} />
      <div className={s.introEdition}>NOVAX · ORGANIZATION INTELLIGENCE · NO. 01</div>
      <div className={s.introHead}>
        <div>
          <span className={s.introKicker}>{uiText("创刊导读")}</span>
          <h2 id="briefing-intro-title">{uiText("让组织的重要变化")}<br />{uiText("主动来到你面前")}</h2>
        </div>
        <Seal />
      </div>
      <div className={s.introRule} />
      <div className={s.introColumns}>
        <div className={s.introLead}>
          <p><span>{uiText("简")}</span>{uiText("报会定期汇总整个组织在 NovaX 中的使用情况，也会根据组织已经连接的数据库、BI、Git、邮箱等底层数据，发现值得关注的业务感知。")}</p>
          <p>{uiText("它还会持续关注竞对的紧急或重要情报，以及行业和政策方向的大新闻，把真正需要组织关注的变化集中推送到这里。")}</p>
        </div>
        <div className={s.introAgentPanel}>
          <div className={s.introAgentLine}>
            <i style={{ background: C.sky }}>{uiText("数")}</i>
            <i style={{ background: C.tomato }}>{uiText("竞")}</i>
            <i style={{ background: C.olive }}>{uiText("策")}</i>
          </div>
          <strong>{uiText("每篇内容都来自")}<br />{uiText("组织里的某一位 Agent")}</strong>
          <p>{uiText("读完后可以直接进入它的对话，继续追问原因、影响和下一步行动。")}</p>
        </div>
      </div>
      <blockquote>{uiText("现在先为你的组织生成第一期。后续随着信息和连接不断补充，NovaX 会继续在这里为你推送值得关注的内容。")}</blockquote>
      <footer className={s.introFooter}>
        <span>{uiText("同一组织 · 同一份简报 · 持续沉淀")}</span>
        <button onClick={onEnter}>{currentReady ? uiText("进入第一期") : uiText("揭开第一期")} <b>→</b></button>
      </footer>
    </section>
  </div>;
}

function BriefingLoading() {
  const stages = ['正在读取组织最近的使用情况', '正在整理组织数据与知识', '正在由 Agent 团队编撰第一期'];
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setStage((value) => Math.min(value + 1, stages.length - 1)), 760);
    return () => window.clearInterval(timer);
  }, [stages.length]);
  return <div className={s.loadingPage}>
    <div className={s.loadingPaper}>
      <span className={s.loadingEdition}>THE BRIEF · NO. 01</span>
      <Seal />
      <h2>{uiText("正在编撰组织简报")}</h2>
      <p>{uiText(stages[stage])}…</p>
      <div className={s.loadingRule}><span style={{ width: `${((stage + 1) / stages.length) * 100}%` }} /></div>
    </div>
  </div>;
}

function Poster({ layout, onOpen }: { layout: PosterLayout; onOpen: (item: BriefingItem) => void }) {
  const { item, solid, span, row, big, delay } = layout;
  const color = kindColor(item.kind);
  const onColor = solid === 'color';
  const onDark = solid === 'dark';
  const textColor = onColor || onDark ? C.paper : C.ink;
  const background = onDark
    ? 'linear-gradient(150deg,#26241a,#16140d)'
    : onColor ? color : C.paperLt;
  const halftone = onDark
    ? { backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1.4px)', backgroundSize: '5px 5px' }
    : { backgroundImage: 'radial-gradient(rgba(0,0,0,0.16) 1px, transparent 1.4px)', backgroundSize: '5px 5px', mixBlendMode: 'multiply' as const };
  const bigColor = onColor ? C.paper : onDark
    ? (color === C.olive ? C.oliveLt : color === C.tomato ? C.tomatoLt : color)
    : color;

  return <article
    className={s.poster}
    onClick={() => onOpen(item)}
    style={{
      gridColumn: `span ${span}`,
      gridRow: `span ${row}`,
      animationDelay: `${delay}s`,
      background,
      color: textColor,
      padding: big ? '24px 26px' : '18px 20px',
    }}
  >
    {(onColor || onDark) && <div className={s.posterHalftone} style={halftone} />}
    <div className={s.posterShine} />
    <div className={s.posterGhost} style={{
      right: -14,
      bottom: -30,
      fontSize: big ? 200 : 130,
      color: onColor ? 'rgba(255,255,255,0.2)' : color,
      opacity: onColor ? 1 : onDark ? 0.18 : 0.12,
    }}>{String(item.id + 1).padStart(2, '0')}</div>
    {big && <div className={s.posterRing} style={{
      left: '52%',
      top: '18%',
      width: 200,
      height: 200,
      border: `2px solid ${onDark ? color : 'rgba(255,255,255,0.4)'}`,
    }} />}
    <div className={s.posterContent}>
      <KindTag kind={item.kind} sm onDark={onColor || onDark} />
      <h2 className={big ? s.posterTitleBig : s.posterTitle}>{item.title}</h2>
      {big && <p className={s.posterTeaser}>{item.teaser}</p>}
    </div>
    <div className={s.posterSpacer} />
    <div className={s.posterFooter}>
      <span className={big ? s.posterMetricBig : s.posterMetric} style={{ color: bigColor }}>{item.big}</span>
      <span className={s.posterMetricLabel}>{item.bigK}</span>
      <span className={s.posterFooterSpacer} />
      <AgentDot icon={item.ag.glyph} color={item.ag.color} size={20} onDark={onColor || onDark} />
      <span className={s.posterOpen} style={{ color: textColor }}>{uiText("翻开 →")}</span>
    </div>
  </article>;
}

function Reader({ item, canDiscuss, onClose, onDiscuss }: {
  item: BriefingItem | null;
  canDiscuss: boolean;
  onClose: () => void;
  onDiscuss: () => void;
}) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  if (!item) return null;
  const color = kindColor(item.kind);

  return <>
    <button className={s.readerBack} onClick={onClose} aria-label={uiText("关闭简报")} />
    <section className={s.readerSheet} role="dialog" aria-modal="true" aria-labelledby="briefing-reader-title">
      <header className={s.readerHeader} style={{ background: color }}>
        <div className={s.readerHeaderHalftone} />
        <div className={s.readerHeaderGhost}>{String(item.id + 1).padStart(2, '0')}</div>
        <button onClick={onClose} className={s.readerClose} aria-label={uiText("关闭")}>×</button>
        <div className={s.readerHeaderContent}>
          <KindTag kind={item.kind} onDark />
          <h1 id="briefing-reader-title" className={s.readerTitle}>{item.title}</h1>
          <div className={s.readerMeta}>
            <AgentDot icon={item.ag.glyph} color={item.ag.color} size={26} onDark />
            <span className={s.readerMetaText}>{item.ag.name} · {item.time} · {item.read}</span>
          </div>
        </div>
      </header>
      <div className={s.readerBody}>
        <main>
          <div className={s.readerSignals}>
            {item.signals.map((signal) => <div key={`${signal.k}-${signal.v}`} className={s.signalCard}>
              <div className={s.signalValue} style={{ color: signal.c }}>{signal.v}</div>
              <div className={s.signalKey}>{signal.k}</div>
            </div>)}
          </div>
          <div className={s.readerMarkdown}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.full.join('\n\n')}</ReactMarkdown>
          </div>
          {item.attach.length > 0 && <div className={s.readerAttachments}>
            {item.attach.map((attachment) => <div key={attachment} className={s.attachChip}>
              <span style={{ width: 8, height: 8, background: color }} />
              <span className={s.attachName}>{attachment}</span>
            </div>)}
          </div>}
        </main>
        <aside className={s.takeawaysRail}>
          <div className={s.takeawaysLabel}>{uiText("要点速读")}</div>
          <div className={s.takeawaysList}>
            {item.points.map((point, index) => <div key={point} className={s.takeawayItem}>
              <span className={s.takeawayNum} style={{ background: color }}>{index + 1}</span>
              <span className={s.takeawayText}>{point}</span>
            </div>)}
          </div>
          <button className={s.discussBtn} onClick={onDiscuss} disabled={!canDiscuss}>
            <AgentDot icon={item.ag.glyph} color={item.ag.color} size={24} />
            {canDiscuss ? `与 ${item.ag.name} 讨论 →` : uiText("对应 Agent 暂不可用")}
          </button>
        </aside>
      </div>
    </section>
  </>;
}

export function BriefingsPage() {
  const token = useStore((state) => state.token);
  const bots = useStore((state) => state.bots);
  const setView = useStore((state) => state.setView);
  const getOrCreateBotSession = useStore((state) => state.getOrCreateBotSession);
  const setComposerDraft = useStore((state) => state.setComposerDraft);
  const [briefing, setBriefing] = useState<ApiBriefing | null>(null);
  const [openItem, setOpenItem] = useState<BriefingItem | null>(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const ensureFirstIssue = useCallback(async () => {
    if (!token) return;
    setGenerating(true);
    setError('');
    try {
      const [current] = await Promise.all([
        briefingRequest(token, '/api/briefings/ensure-current', 'POST'),
        wait(2500),
      ]);
      setBriefing(current);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : uiText('简报生成失败'));
    } finally {
      setGenerating(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const initialize = async () => {
      try {
        const current = await briefingRequest(token, '/api/briefings/latest');
        if (cancelled) return;
        setBriefing(current);
        if (!hasSeenIntro()) setIntroOpen(true);
        else if (!current || current.formatVersion < BRIEFING_FORMAT_VERSION) {
          setLoading(false);
          await ensureFirstIssue();
          return;
        }
      } catch (requestError) {
        if (!cancelled) setError(requestError instanceof Error ? requestError.message : uiText('简报加载失败'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void initialize();
    return () => { cancelled = true; };
  }, [ensureFirstIssue, token]);

  const items = useMemo(() => (briefing?.items || []).map((item, index) => {
    const assigned = bots.find((bot) => bot.name === item.ag?.name);
    const fallback = bots[index % Math.max(bots.length, 1)];
    if (assigned || !fallback) return item;
    return { ...item, ag: { ...item.ag, name: fallback.name, glyph: Array.from(fallback.name)[0] || 'N' } };
  }), [bots, briefing?.items]);

  const enterBriefing = useCallback(async () => {
    markIntroSeen();
    setIntroOpen(false);
    if (!briefing || briefing.formatVersion < BRIEFING_FORMAT_VERSION) await ensureFirstIssue();
  }, [briefing, ensureFirstIssue]);

  const discussItem = useCallback((item: BriefingItem) => {
    if (!bots.some((bot) => bot.name === item.ag.name)) return;
    const sessionId = getOrCreateBotSession(item.ag.name);
    setComposerDraft(sessionId, `${uiText('我想继续讨论这篇组织简报：')}${item.title}。${uiText('请结合简报结论，进一步说明原因、可能影响，以及我们接下来最值得采取的行动。')}`);
    setOpenItem(null);
    setView('chat');
  }, [bots, getOrCreateBotSession, setComposerDraft, setView]);

  if (generating) return <BriefingLoading />;

  const tickerSignals = briefing?.tickerSignals || [];
  const layouts = buildLayouts(items);

  return <div className={s.page}>
    <header className={s.masthead}>
      <div className={s.mastheadInner}>
        <div className={s.mastheadTitle}>{uiText("简报")}<span className={s.mastheadSub}>The Brief</span></div>
        <Seal />
        <div className={s.mastheadSpacer} />
        <button className={s.guideButton} onClick={() => setIntroOpen(true)}>{uiText("创刊导读")}</button>
        <div className={s.mastheadRight}>
          <div className={s.mastheadClock}><Clock /></div>
          <div className={s.mastheadMeta}>{uiText("第 1 期 · 由你的 Agent 团队编撰 · 本期")} {items.length} {uiText("篇")}</div>
        </div>
      </div>
      <div className={s.mastheadRule} />
      {tickerSignals.length > 0 && <div className={s.tickerWrap}>
        <span className={s.tickerLabel}>{uiText("组织信号")}</span>
        <div className={s.tickerOverflow}>
          <div className={s.tickerTrack}>
            {[...tickerSignals, ...tickerSignals].map((signal, index) => <span key={`${signal.key}-${index}`} className={s.tickerItem}>
              <span className={s.tickerDot} style={{ background: signal.color }} />
              <span className={s.tickerKey}>{signal.key}</span>
              <span className={s.tickerVal}>{signal.value}</span>
            </span>)}
          </div>
        </div>
      </div>}
    </header>

    {error && <div className={s.errorBanner}><span>{uiText(error)}</span><button onClick={() => void ensureFirstIssue()}>{uiText("重新尝试")}</button></div>}
    {loading ? <div className={s.pageState}>{uiText("加载中…")}</div> : layouts.length > 0
      ? <main className={s.mosaic}>{layouts.map((layout) => <Poster key={layout.item.id} layout={layout} onOpen={setOpenItem} />)}</main>
      : <div className={s.pageState}>{uiText("第一期正在准备中")}</div>}

    {introOpen && <BriefingIntro
      currentReady={!!briefing && briefing.formatVersion >= BRIEFING_FORMAT_VERSION}
      onEnter={() => void enterBriefing()}
    />}
    <Reader
      item={openItem}
      canDiscuss={!!openItem && bots.some((bot) => bot.name === openItem.ag.name)}
      onClose={() => setOpenItem(null)}
      onDiscuss={() => { if (openItem) discussItem(openItem); }}
    />
  </div>;
}
